import { useEffect, useState, useRef, useMemo } from 'react';
import { Crosshair as CrosshairIcon, Square as SquareIcon, Settings2, Trash2, X } from 'lucide-react';
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { useFlowaesStore } from '../store/useFlowaesStore';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface RadialMenuProps {
  position: { x: number; y: number } | null;
  onClose: () => void;
  timeframe: string;
  setTimeframe: (tf: string) => void;
  interactionMode: string;
  setInteractionMode: (mode: 'orbit' | 'crosshair' | 'zone' | 'time') => void;
  setZones: (zones: any[]) => void;
  setSelectedZoneId: (id: string | null) => void;
  visibleTimeframes: string[];
  setVisibleTimeframes: (tfs: string[]) => void;
  isLoadingData?: boolean;

}



export default function RadialMenu({
  position,
  onClose,
  timeframe,
  setTimeframe,
  interactionMode,
  setInteractionMode,
  setZones,
  setSelectedZoneId,
  visibleTimeframes,
  setVisibleTimeframes,
  isLoadingData

}: RadialMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [view, setView] = useState<'menu' | 'timeframe-config'>('menu');
  const [internalPos, setInternalPos] = useState<{x: number, y: number} | null>(position);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const dotRef = useRef<HTMLDivElement>(null);
  const openTimeRef = useRef(Date.now());
  const stateRef = useRef({ hoveredIndex, onClose });

  useEffect(() => {
    if (position) {
      openTimeRef.current = Date.now();
      setInternalPos(position);
      setIsOpen(true);
      const timer = setTimeout(() => {
        setIsRendered(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsRendered(false);
      const timer = setTimeout(() => {
        setIsOpen(false);
        setView('menu'); // Reset back to menu when closed
        if (useFlowaesStore.getState()?.activeGesture === 'radial_menu') {
          useFlowaesStore.getState()?.setActiveGesture?.(null);
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [position]);

  useEffect(() => {
    return () => {
      if (useFlowaesStore.getState()?.activeGesture === 'radial_menu') {
        useFlowaesStore.getState()?.setActiveGesture?.(null);
      }
    };
  }, []);

  const allSupportedTimeframes = useMemo(() => ['1s', '1m', '2m', '3m', '5m', '10m', '15m', '20m', '30m', '1h', '2h', '4h', '6h', '8h', '12h', '1d', '2d', '3d', '1w', '1M', '3M', '6M', '1Y'], []);
  
  const sortedTimeframes = useMemo(() => {
    return [...visibleTimeframes].sort((a, b) => {
      return allSupportedTimeframes.indexOf(a) - allSupportedTimeframes.indexOf(b);
    });
  }, [visibleTimeframes, allSupportedTimeframes]);

  // Tools to include in the radial menu
  const tools = useMemo(() => [
    {
      id: 'crosshair',
      icon: <CrosshairIcon size={18} />,
      isActive: interactionMode === 'crosshair',
      isDisabled: true,
      action: () => setInteractionMode(interactionMode === 'crosshair' ? 'orbit' : 'crosshair')
    },
    {
      id: 'clearZones',
      icon: <Trash2 size={18} />,
      isActive: false,
      isDisabled: true,
      action: () => {
        setZones([]);
        setSelectedZoneId(null);
        setInteractionMode('orbit');
      }
    },
    {
      id: 'settings',
      icon: <Settings2 size={18} />,
      isActive: false,
      action: () => setView('timeframe-config')
    }
  ], [interactionMode, setInteractionMode, setZones, setSelectedZoneId]);

  // We want to distribute items in a circle. 
  const allItems = useMemo(() => [
    ...sortedTimeframes.map(tf => ({
      type: 'timeframe',
      id: tf,
      label: tf.toUpperCase(),
      isActive: timeframe === tf,
      action: () => setTimeframe(tf)
    })),
    ...tools.map(t => ({
      type: 'tool',
      ...t
    }))
  ], [sortedTimeframes, timeframe, setTimeframe, tools]);

  stateRef.current = { hoveredIndex, onClose };

  useEffect(() => {
    if (!isOpen) return;





    const handlePointerMove = (e: PointerEvent) => {
      if (view === 'timeframe-config') return;
      
      const currentActivePos = position || internalPos;
      if (!currentActivePos) return;
      
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const radius = isMobile ? 55 : 85;
      const outerRadius = isMobile ? 110 : 150;

      let adjustedX = currentActivePos.x;
      let adjustedY = currentActivePos.y;
      
      if (adjustedX < outerRadius) adjustedX = outerRadius;
      if (adjustedX > window.innerWidth - outerRadius) adjustedX = window.innerWidth - outerRadius;
      if (adjustedY < outerRadius) adjustedY = outerRadius;
      if (adjustedY > window.innerHeight - outerRadius) adjustedY = window.innerHeight - outerRadius;

      const angleStep = (2 * Math.PI) / allItems.length;
      const startAngle = -Math.PI / 2;

      const dx = e.clientX - adjustedX;
      const dy = e.clientY - adjustedY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      let newHoveredIndex = null;
      if (distance >= radius * 0.4) {
        let angle = Math.atan2(dy, dx);
        let relativeAngle = angle - startAngle;
        while (relativeAngle < 0) relativeAngle += 2 * Math.PI;
        while (relativeAngle >= 2 * Math.PI) relativeAngle -= 2 * Math.PI;
        
        // Offset by half a step to center the slice on the angle
        relativeAngle += angleStep / 2;
        while (relativeAngle >= 2 * Math.PI) relativeAngle -= 2 * Math.PI;
        
        newHoveredIndex = Math.floor(relativeAngle / angleStep) % allItems.length;
      }

      setHoveredIndex(newHoveredIndex);
      stateRef.current.hoveredIndex = newHoveredIndex; // keep state ref updated

      if (dotRef.current) {
        if (newHoveredIndex !== null) {
          const hAngle = startAngle + newHoveredIndex * angleStep;
          const itemRadius = (radius + outerRadius) / 2;
          const dotX = Math.cos(hAngle) * itemRadius;
          const dotY = Math.sin(hAngle) * itemRadius;
          dotRef.current.style.transform = `translate(-50%, -50%) translate(${dotX}px, ${dotY}px)`;
        } else {
          // Free move
          const maxDist = radius * 0.7;
          const freeDist = Math.min(distance, maxDist);
          const freeAngle = Math.atan2(dy, dx);
          const dotX = Math.cos(freeAngle) * freeDist;
          const dotY = Math.sin(freeAngle) * freeDist;
          dotRef.current.style.transform = `translate(-50%, -50%) translate(${dotX}px, ${dotY}px)`;
        }
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      e.stopPropagation();
      const { hoveredIndex, onClose } = stateRef.current;
      
      // If we are configuring timeframes, don't auto-close on pointer up
      if (view === 'timeframe-config') return;

      if (hoveredIndex !== null && allItems[hoveredIndex]) {
        const item = allItems[hoveredIndex] as any;
        if (item.isDisabled) return;
        item.action();
        if (item.id !== 'settings') {
          onClose();
        }
      } else {
        if (Date.now() - openTimeRef.current > 300) {
          onClose();
        }
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isOpen, view, allItems, position, internalPos]);

  const activePos = position || internalPos;

  if (!activePos || !isOpen) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const radius = isMobile ? 55 : 85;
  const outerRadius = isMobile ? 110 : 150;
  const iconSize = isMobile ? "w-10 h-10" : "w-12 h-12";
  const fontSize = isMobile ? "text-[10px]" : "text-[12px]";

  // Make sure it stays on screen
  let adjustedX = activePos.x;
  let adjustedY = activePos.y;
  
  if (adjustedX < outerRadius) adjustedX = outerRadius;
  if (adjustedX > window.innerWidth - outerRadius) adjustedX = window.innerWidth - outerRadius;
  if (adjustedY < outerRadius) adjustedY = outerRadius;
  if (adjustedY > window.innerHeight - outerRadius) adjustedY = window.innerHeight - outerRadius;

  const angleStep = (2 * Math.PI) / allItems.length;
  // Start from top (-Math.PI / 2)
  const startAngle = -Math.PI / 2;

  if (view === 'timeframe-config') {
    const allSupportedTimeframes = ['1s', '1m', '2m', '3m', '5m', '10m', '15m', '20m', '30m', '1h', '2h', '4h', '6h', '8h', '12h', '1d', '2d', '3d', '1w', '1M', '3M', '6M', '1Y'];
    return (
      <div 
        className="fixed inset-0 z-[2000] pointer-events-auto flex items-center justify-center bg-black/45 backdrop-blur-xs"
        onClick={(e) => {
          e.stopPropagation();
          if (Date.now() - openTimeRef.current < 300) return;
          onClose();
        }}
      >
        <div 
          className="bg-[#0a0e27]/95 border border-[#00d9ff]/40 p-5 rounded-3xl shadow-[0_0_50px_rgba(0,217,255,0.25)] backdrop-blur-md flex flex-col gap-4 max-w-[90vw] w-[310px] text-center"
          style={{
            position: 'absolute',
            left: adjustedX,
            top: adjustedY,
            transform: `translate(-50%, -50%) scale(${isRendered ? 1 : 0.3})`,
            transitionProperty: 'transform, opacity',
            transitionDuration: isRendered ? '0.35s, 0.25s' : '0.2s, 0.2s',
            transitionTimingFunction: isRendered ? 'cubic-bezier(0.34, 1.56, 0.64, 1), ease-out' : 'cubic-bezier(0.4, 0, 0.2, 1), ease-out',
            opacity: isRendered ? 1 : 0,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center border-b border-[#00d9ff]/20 pb-2.5">
            <div className="flex items-center gap-2">
              <h3 className="text-[#00d9ff] text-[11px] font-black uppercase tracking-[2px] text-left">
                Configurar Períodos
              </h3>
              <button 
                className="w-4 h-4 rounded-full bg-white/10 text-white/50 text-[9px] flex items-center justify-center hover:bg-white/20 transition-all cursor-pointer"
                onClick={() => setShowTutorial(!showTutorial)}
              >
                ?
              </button>
            </div>
            <button
              onClick={() => setView('menu')}
              className="text-[#ff3d67] p-1 bg-[#ff3d67]/10 rounded-full hover:bg-[#ff3d67]/20 transition-all cursor-pointer flex items-center justify-center"
            >
              <X size={12} />
            </button>
          </div>

          {showTutorial && (
            <div className="text-[10px] text-white/70 bg-white/5 p-2 rounded-lg text-left border border-white/10 leading-tight">
              Selecione quais tempos gráficos você deseja ter acesso rápido na roleta. Você pode selecionar até 10 opções. As opções em azul claro já estão visíveis na roleta.
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 py-1 max-h-[220px] overflow-y-auto no-scrollbar">
            {allSupportedTimeframes.map((tf) => {
              const isActive = visibleTimeframes.includes(tf);
              return (
                <button
                  key={tf}
                  onClick={() => {
                    if (isActive) {
                      if (visibleTimeframes.length > 1) {
                        setVisibleTimeframes(visibleTimeframes.filter(t => t !== tf));
                      }
                    } else {
                      if (visibleTimeframes.length < 10) {
                        const newTfs = [...visibleTimeframes, tf];
                        setVisibleTimeframes(allSupportedTimeframes.filter(t => newTfs.includes(t)));
                      }
                    }
                  }}
                  className={cn(
                    "py-1.5 text-[10px] font-bold rounded-lg border uppercase transition-all duration-200 cursor-pointer",
                    isActive
                      ? "border-[#00d9ff]/80 bg-[#00d9ff]/20 text-[#00d9ff] shadow-[0_0_8px_rgba(0,217,255,0.2)]"
                      : "border-[#00d9ff]/30 bg-[#0a0e27]/80 text-[#666666] hover:bg-[#00d9ff]/10 hover:text-[#00d9ff]"
                  )}
                >
                  {tf}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setView('menu')}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#00d9ff] to-[#0099ff] text-[#0a0e27] font-black text-[10px] uppercase tracking-widest hover:brightness-110 active:scale-98 transition-all cursor-pointer"
          >
            Confirmar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[2000] pointer-events-auto"

      onContextMenu={(e) => {
         e.preventDefault();
      }}
    >
      <div 
        className="absolute pointer-events-auto shadow-[0_0_50px_rgba(0,0,0,0.85)] rounded-full origin-center"
        style={{
          left: adjustedX,
          top: adjustedY,
          width: 0,
          height: 0,
        }}
      >
        {/* Inner circle (hole) */}
        <div 
          className="absolute top-1/2 left-1/2 rounded-full bg-transparent border border-[#00d9ff]/20 z-10 flex items-center justify-center" 
          style={{ 
            width: radius * 2 - (isMobile ? 15 : 20), 
            height: radius * 2 - (isMobile ? 15 : 20),
            transform: `translate(-50%, -50%) scale(${isRendered ? 1 : 0.3})`,
            transitionProperty: 'transform, opacity',
            transitionDuration: isRendered ? '0.35s, 0.25s' : '0.2s, 0.2s',
            transitionTimingFunction: isRendered ? 'cubic-bezier(0.34, 1.56, 0.64, 1), ease-out' : 'cubic-bezier(0.4, 0, 0.2, 1), ease-out',
            opacity: isRendered ? 1 : 0,
          }}
        >
          <X className={cn("text-[#ff3d67] transition-opacity relative z-0", hoveredIndex === null ? "opacity-100" : "opacity-0")} size={24} />
        </div>
        {/* Menu items */}
        {allItems.map((item, index) => {
          const angle = startAngle + index * angleStep;
          const itemRadius = (radius + outerRadius) / 2; // middle of the ring
          
          const x = Math.cos(angle) * itemRadius;
          const y = Math.sin(angle) * itemRadius;
          const isHovered = hoveredIndex === index;
          return (
            <button
              key={item.id}
              onClick={(e) => {
                e.stopPropagation();
                item.action();
                if (item.id !== 'settings') {
                  onClose();
                }
              }}
              className={cn(
                "absolute flex items-center justify-center rounded-full z-20 pointer-events-none",
                iconSize,
                item.isActive
                  ? "bg-[#00d9ff]/40 text-[#00d9ff] font-bold shadow-[0_0_20px_rgba(0,217,255,0.6)]"
                  : isHovered
                    ? "bg-[#00d9ff]/25 text-[#00d9ff] shadow-[0_0_15px_rgba(0,217,255,0.4)]"
                    : "bg-[#0a0e27]/80 text-[#666666]",
                (isLoadingData && item.type === "timeframe") || (item as any).isDisabled ? "opacity-30 cursor-not-allowed" : ""
              )}
              style={{
                left: x,
                top: y,
                transform: `translate(-50%, -50%) scale(${isRendered ? (isHovered ? 1.3 : (item.isActive ? 1.15 : 1)) : 0})`,
                transitionProperty: 'transform, opacity, background-color, border-color, box-shadow',
                transitionDuration: isRendered ? '0.35s, 0.25s, 0.15s, 0.15s, 0.15s' : '0.2s, 0.2s, 0.15s, 0.15s, 0.15s',
                transitionTimingFunction: isRendered ? 'cubic-bezier(0.34, 1.56, 0.64, 1), ease-out, ease, ease, ease' : 'cubic-bezier(0.4, 0, 0.2, 1), ease-out, ease, ease, ease',
                transitionDelay: isRendered ? `${index * 15}ms, ${index * 15}ms, 0ms, 0ms, 0ms` : '0ms, 0ms, 0ms, 0ms, 0ms',
                opacity: isRendered ? 1 : 0,
              }}
            >
              {item.type === 'timeframe' ? (
                <span className={cn("uppercase tracking-wider", fontSize)}>{(item as any).label}</span>
              ) : (
                (item as any).icon
              )}
            </button>
          );
        })}

        {/* Pointer ring */}
        {isRendered && (
          <div
            ref={dotRef}
            className={cn(
              "absolute top-1/2 left-1/2 rounded-full z-[30] pointer-events-none flex items-center justify-center border-2 border-[#00d9ff]",
              hoveredIndex !== null ? "bg-transparent shadow-[0_0_15px_rgba(0,217,255,0.4)_inset,0_0_15px_rgba(0,217,255,0.4)]" : "bg-transparent shadow-[0_0_10px_rgba(0,217,255,0.8)]"
            )}
            style={{
              width: hoveredIndex !== null ? (isMobile ? '46px' : '54px') : '30px',
              height: hoveredIndex !== null ? (isMobile ? '46px' : '54px') : '30px',
              transform: 'translate(-50%, -50%)',
              transitionProperty: 'width, height, background-color',
              transitionDuration: '0.15s',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
             {hoveredIndex === null && <div className="w-1 h-1 bg-[#00d9ff] rounded-full" />}
          </div>
        )}

        {/* Ring background SVG to make it look like a solid ring */}
        <svg 
          className="absolute top-1/2 left-1/2 pointer-events-none drop-shadow-xl"
          width={outerRadius * 2} 
          height={outerRadius * 2}
          style={{
            transform: `translate(-50%, -50%) scale(${isRendered ? 1 : 0.3})`,
            transitionProperty: 'transform, opacity',
            transitionDuration: isRendered ? '0.35s, 0.25s' : '0.2s, 0.2s',
            transitionTimingFunction: isRendered ? 'cubic-bezier(0.34, 1.56, 0.64, 1), ease-out' : 'cubic-bezier(0.4, 0, 0.2, 1), ease-out',
            opacity: isRendered ? 1 : 0,
          }}
        >
          <circle 
            cx={outerRadius} 
            cy={outerRadius} 
            r={(radius + outerRadius) / 2}
            stroke="#0a0e27"
            strokeOpacity="0.85"
            strokeWidth={outerRadius - radius}
            fill="none"
          />
          <circle 
            cx={outerRadius} 
            cy={outerRadius} 
            r={outerRadius}
            stroke="#00d9ff"
            strokeOpacity="0.2"
            strokeWidth="1"
            fill="none"
          />
          <circle 
            cx={outerRadius} 
            cy={outerRadius} 
            r={radius}
            stroke="#00d9ff"
            strokeOpacity="0.2"
            strokeWidth="1"
            fill="none"
          />
        </svg>
      </div>
    </div>
  );
}
